import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrgHall } from "@/hooks/useOrgHall";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { validateCourtAvailability } from "@/lib/validateCourtAvailability";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

import type { ActivityDetail } from "@/hooks/useActivityDetail";

type PaymentModeChoice = "system" | "external";

interface ConnectCourtsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity: ActivityDetail;
}

export default function ConnectCourtsDialog({
  open,
  onOpenChange,
  activity,
}: ConnectCourtsDialogProps) {
  const { t } = useTranslation("activities");
  const { user } = useAuth();
  const { courts } = useOrgHall();
  const qc = useQueryClient();

  const [selectedCourtIds, setSelectedCourtIds] = useState<string[]>([]);
  const [paymentMode, setPaymentMode] = useState<PaymentModeChoice>("system");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check court availability for the activity time slot
  const dateTimeStart = useMemo(() => {
    if (!activity.date || !activity.start_time) return null;
    return `${activity.date}T${activity.start_time}:00`;
  }, [activity.date, activity.start_time]);

  const dateTimeEnd = useMemo(() => {
    if (!activity.date || !activity.end_time) return null;
    return `${activity.date}T${activity.end_time}:00`;
  }, [activity.date, activity.end_time]);

  const { data: bookedCourtIds = [] } = useQuery({
    queryKey: ["booked-courts-connect", activity.date, activity.start_time, activity.end_time],
    queryFn: async () => {
      if (!dateTimeStart || !dateTimeEnd) return [];
      const { data } = await supabase
        .from("bookings")
        .select("court_id")
        .neq("status", "cancelled")
        .lt("start_time", dateTimeEnd)
        .gt("end_time", dateTimeStart);
      return [...new Set((data ?? []).map((b) => b.court_id))];
    },
    enabled: open && !!dateTimeStart && !!dateTimeEnd,
  });

  const toggleCourt = (courtId: string) => {
    setSelectedCourtIds((prev) =>
      prev.includes(courtId)
        ? prev.filter((c) => c !== courtId)
        : [...prev, courtId]
    );
  };

  const handleSubmit = async () => {
    if (!user || selectedCourtIds.length === 0) return;
    setIsSubmitting(true);

    try {
      const startDT = new Date(`${activity.date}T${activity.start_time}`);
      const endDT = new Date(`${activity.date}T${activity.end_time}`);
      const durMin = Math.round(
        (endDT.getTime() - startDT.getTime()) / 60000
      );
      const groupId =
        selectedCourtIds.length > 1 ? crypto.randomUUID() : null;

      // PR-AJ Layer B — pre-INSERT availability check per court.
      for (const courtId of selectedCourtIds) {
        const availability = await validateCourtAvailability(supabase, {
          courtId,
          startTime: startDT,
          endTime: endDT,
        });
        if (!availability.available) {
          toast.error(availability.message);
          setIsSubmitting(false);
          return;
        }
      }

      // Create booking rows
      // PR-AN: created_by = current user (activity organizer/admin).
      const bookingInserts = selectedCourtIds.map((courtId) => ({
        court_id: courtId,
        user_id: user.id,
        created_by: user.id,
        start_time: startDT.toISOString(),
        end_time: endDT.toISOString(),
        duration_minutes: durMin,
        is_open_match: true,
        activity_id: activity.id,
        max_players: activity.spots_total,
        price_per_hour: 0,
        price_total: 0,
        booking_group_id: groupId,
      }));

      await supabase.from("bookings").insert(bookingInserts as any);

      // Update activities with new court and payment mode
      await supabase
        .from("activities")
        .update({
          court_id: selectedCourtIds[0],
          payment_mode: paymentMode,
          organizer_pays_court: paymentMode === "external",
        } as any)
        .eq("id", activity.id);

      qc.invalidateQueries({ queryKey: ["activity-detail", activity.id] });
      qc.invalidateQueries({ queryKey: ["activities"] });

      toast.success(t("activities:courtsConnected", "Banor kopplade!"));
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message ?? "Kunde inte koppla banor");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("activities:connectCourts", "Koppla banor")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Court picker */}
          <div className="space-y-1.5">
            <Label className="text-xs">
              {t("activities:courts", "Banor")}
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {courts.map((c: any) => {
                const isSelected = selectedCourtIds.includes(c.id);
                const isBooked = bookedCourtIds.includes(c.id);
                return (
                  <Badge
                    key={c.id}
                    variant={
                      isSelected ? "default" : isBooked ? "secondary" : "outline"
                    }
                    className={cn(
                      "text-xs",
                      isBooked && "opacity-40 cursor-not-allowed line-through",
                      !isBooked && "cursor-pointer"
                    )}
                    onClick={() => {
                      if (isBooked) return;
                      toggleCourt(c.id);
                    }}
                  >
                    {c.name}
                    {isBooked && (
                      <span className="ml-1 text-[9px]">
                        ({t("activities:courtBooked", "Bokad")})
                      </span>
                    )}
                  </Badge>
                );
              })}
            </div>
          </div>

          {/* Payment mode selector */}
          <div className="space-y-2">
            <Label className="text-xs">
              {t("activities:paymentModeLabel", "Betalningsmodell")}
            </Label>
            <div className="grid grid-cols-1 gap-2">
              {([
                {
                  value: "system" as const,
                  icon: "\uD83D\uDCB3",
                  label: t(
                    "activities:paymentModeSystem",
                    "Ta betalt via systemet"
                  ),
                },
                {
                  value: "external" as const,
                  icon: "\uD83E\uDD1D",
                  label: t(
                    "activities:paymentModeExternal",
                    "Hantera betalning själv"
                  ),
                },
              ] as const).map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  className={cn(
                    "flex items-center gap-2 rounded-lg border p-2.5 text-left text-sm transition-colors",
                    paymentMode === mode.value
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                      : "border-border hover:border-primary/30"
                  )}
                  onClick={() => setPaymentMode(mode.value)}
                >
                  <span>{mode.icon}</span>
                  <span className="font-medium">{mode.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common:cancel", "Avbryt")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedCourtIds.length === 0}
          >
            {isSubmitting && (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            )}
            {t("activities:connectCourts", "Koppla banor")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
